import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import {
  getMascotPosterSrc,
  getMascotVideoCandidates,
  MASCOT_IDLE_POSTER,
} from './mascotAssets';

// Landscape 16:9 clips (1280x720). Width-driven and self-start so the frame
// keeps its aspect ratio (never stretched by the sibling text column) and the
// card stays short at any Chrome zoom level.
const FRAME_HORIZONTAL =
  'w-[clamp(140px,42%,240px)] aspect-video self-start rounded-lg overflow-hidden  ring-2 ring-forest/40 bg-canvas/5 dark:bg-black/40 shrink-0';

// Larger horizontal frame (same aspect) for steps that need a bigger mascot.
const FRAME_HORIZONTAL_LARGE =
  'w-[clamp(220px,58%,360px)] aspect-video self-start rounded-lg overflow-hidden  ring-2 ring-forest/40 bg-canvas/5 dark:bg-black/40 shrink-0';

// Full-width hero video stacked above the text (story cards) — landscape.
const FRAME_HERO =
  'w-full aspect-video max-h-[min(40vh,260px)] rounded-lg overflow-hidden  ring-2 ring-forest/40 bg-canvas/5 dark:bg-black/40 shrink-0';

// Welcome / guide clips — fill the card width; card is sized to a true 16:9 frame.
const FRAME_WELCOME =
  'w-full aspect-video rounded-lg overflow-hidden  ring-2 ring-forest/40 bg-canvas/5 dark:bg-black/40 shrink-0';

// Side-dock video for float layouts — scales with viewport so any Chrome zoom still fits.
const FRAME_HERO_LARGE =
  'w-full aspect-video max-h-[min(38vh,100%)] rounded-lg overflow-hidden  ring-2 ring-forest/40 bg-canvas/5 dark:bg-black/40 shrink-0';

// Compact stacked video for narrow side-dock cards.
const FRAME_STACK =
  'w-full aspect-video max-h-[9.5rem] rounded-lg overflow-hidden  ring-2 ring-forest/40 bg-canvas/5 dark:bg-black/40 shrink-0';

const TourMascot = ({
  stepId,
  language,
  label,
  volume = 1,
  onVolumeChange,
  variant = 'horizontal',
}) => {
  const frameClass =
    variant === 'heroLarge'
      ? FRAME_HERO_LARGE
      : variant === 'welcome'
        ? FRAME_WELCOME
        : variant === 'hero'
          ? FRAME_HERO
          : variant === 'stack'
            ? FRAME_STACK
            : variant === 'horizontalLarge'
              ? FRAME_HORIZONTAL_LARGE
              : FRAME_HORIZONTAL;
  const mediaFit = 'object-cover';  const videoRef = useRef(null);
  const candidates = useMemo(
    () => (stepId ? getMascotVideoCandidates(stepId, language) : []),
    [stepId, language]
  );
  const poster = stepId ? getMascotPosterSrc(stepId) : MASCOT_IDLE_POSTER;

  const [candidateIndex, setCandidateIndex] = useState(0);
  const [showPoster, setShowPoster] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [posterSrc, setPosterSrc] = useState(poster);
  const [exhausted, setExhausted] = useState(false);
  const [hasVideo, setHasVideo] = useState(false);

  const isMuted = volume <= 0;
  const volumePercent = Math.round(Math.min(1, Math.max(0, volume)) * 100);
  const lastAudibleVolumeRef = useRef(volume > 0 ? volume : 0.75);

  useEffect(() => {
    if (volume > 0) lastAudibleVolumeRef.current = volume;
  }, [volume]);

  useEffect(() => {
    setCandidateIndex(0);
    setShowPoster(false);
    setPosterSrc(poster);
    setExhausted(false);
    setHasVideo(false);
  }, [stepId, language, poster]);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setReduceMotion(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const src = candidates[candidateIndex] || null;

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !hasVideo) return;
    const level = Math.min(1, Math.max(0, volume));
    const wantSound = level > 0;
    el.volume = wantSound ? level : 0;
    el.muted = !wantSound;
    el.currentTime = 0;
    el.play()?.catch(() => {
      if (wantSound) {
        el.muted = true;
        el.play()?.catch(() => {});
      }
    });
  }, [volume, src, hasVideo, stepId]);

  const handleVideoError = () => {
    if (candidateIndex < candidates.length - 1) {
      setCandidateIndex((i) => i + 1);
      return;
    }
    setShowPoster(true);
  };

  if (!stepId) return null;

  // Clip and poster both failed: collapse the frame so the card text stands alone.
  if (exhausted) return null;

  if (reduceMotion || showPoster) {
    return (
      <div className={`relative ${frameClass}`}>
        <img
          src={posterSrc}
          alt=""
          className={`w-full h-full ${mediaFit}`}
          onError={() => {
            if (posterSrc !== MASCOT_IDLE_POSTER) setPosterSrc(MASCOT_IDLE_POSTER);
            else setExhausted(true);
          }}
        />
      </div>
    );
  }

  if (!src) return null;

  return (
    <div className={`relative ${frameClass}`}>
      <video
        ref={videoRef}
        key={src}
        src={src}
        className={`w-full h-full ${mediaFit}`}
        muted={isMuted}
        playsInline
        autoPlay
        loop={isMuted}
        aria-label={label || undefined}
        onLoadedData={() => setHasVideo(true)}
        onError={handleVideoError}
      />
      {hasVideo && onVolumeChange && (
        <div
          className="absolute bottom-0 inset-x-0 z-10 flex items-center gap-1.5 px-2 py-1.5 bg-black/70 "
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => {
              const next = isMuted ? (lastAudibleVolumeRef.current || 0.75) : 0;
              onVolumeChange(next);
              const el = videoRef.current;
              if (el) {
                if (next > 0) {
                  el.muted = false;
                  el.volume = next;
                  el.play()?.catch(() => {});
                } else {
                  el.muted = true;
                  el.volume = 0;
                }
              }
            }}
            aria-label={isMuted ? 'Unmute' : 'Mute'}
            className="shrink-0 p-0.5 rounded hover:bg-white/10 transition-colors"
          >
            {isMuted ? (
              <VolumeX size={12} className="text-white/80" />
            ) : (
              <Volume2 size={12} className="text-white" />
            )}
          </button>
          <input
            type="range"
            min="0"
            max="100"
            step="5"
            value={volumePercent}
            onChange={(e) => {
              const next = Number(e.target.value) / 100;
              onVolumeChange(next);
              const el = videoRef.current;
              if (el && next > 0) {
                el.muted = false;
                el.volume = next;
                el.play()?.catch(() => {});
              }
            }}
            aria-label="Volume"
            className="flex-1 min-w-0 h-1 accent-forest cursor-pointer"
            style={{
              background: `linear-gradient(to right, #34d399 0%, #34d399 ${volumePercent}%, rgba(255,255,255,0.25) ${volumePercent}%, rgba(255,255,255,0.25) 100%)`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TourMascot;
